import { motion } from 'framer-motion';
import { MapPin, Phone, Clock, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageHeader from '@/components/common/PageHeader';
import ConsultationForm from '@/components/common/ConsultationForm';

const offices = {
  'new-delhi-office': {
    name: 'New Delhi Office (Head Office)',
    address: 'Ground Floor 5, Building No. 27B, Khizrabad, New Friends Colony, New Delhi - 110025',
    phone: '+91-9958848998',
    email: 'delhi@zafco.in',
    hours: 'Mon-Sat: 10:00 AM - 7:00 PM',
    mapUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3503.9891427631706!2d77.26438731508035!3d28.5672839824381!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390ce35a44444441%3A0x2bc234578edc9f9!2sNew%20Friends%20Colony%2C%20New%20Delhi%2C%20Delhi!5e0!3m2!1sen!2sin!4v1635678901234!5m2!1sen!2sin',
  },
  'mumbai-office': {
    name: 'Mumbai Office',
    address: 'Coming Soon - Andheri West, Mumbai, Maharashtra',
    phone: '+91-9958848998',
    email: 'mumbai@zafco.in',
    hours: 'Mon-Sat: 10:00 AM - 7:00 PM',
    mapUrl: '',
  },
  'bangalore-office': {
    name: 'Bangalore Office',
    address: 'Coming Soon - Koramangala, Bangalore, Karnataka',
    phone: '+91-9958848998',
    email: 'bangalore@zafco.in',
    hours: 'Mon-Sat: 10:00 AM - 7:00 PM',
    mapUrl: '',
  },
  'hyderabad-office': {
    name: 'Hyderabad Office',
    address: 'Coming Soon - Madhapur, Hyderabad, Telangana',
    phone: '+91-9958848998',
    email: 'hyderabad@zafco.in',
    hours: 'Mon-Sat: 10:00 AM - 7:00 PM',
    mapUrl: '',
  },
};

const OfficePage = ({ officeId }) => {
  const office = offices[officeId] || offices['new-delhi-office'];
  
  const openWhatsApp = () => {
    window.open('https://wa.me/919958848998?text=Hello%20Zafco,%20I%20want%20to%20visit%20your%20office', '_blank');
  };

  return (
    <div data-testid="office-page">
      <PageHeader 
        title={office.name}
        subtitle="Visit us for a personal consultation with our immigration experts"
        breadcrumbs={[{ label: 'Offices' }, { label: office.name }]}
      />

      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-zafco-blue font-montserrat mb-8">
                Contact Information
              </h2>
              
              <div className="space-y-6 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-zafco-blue/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-zafco-blue" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-zafco-blue mb-1">Address</h4>
                    <p className="text-gray-600">{office.address}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-zafco-blue/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-zafco-blue" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-zafco-blue mb-1">Phone</h4>
                    <a href={`tel:${office.phone}`} className="text-gray-600 hover:text-zafco-blue">
                      {office.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-zafco-blue/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-zafco-blue" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-zafco-blue mb-1">Email</h4>
                    <a href={`mailto:${office.email}`} className="text-gray-600 hover:text-zafco-blue">
                      {office.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-zafco-blue/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-zafco-blue" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-zafco-blue mb-1">Working Hours</h4>
                    <p className="text-gray-600">{office.hours}</p>
                  </div>
                </div>
              </div>

              <Button 
                onClick={openWhatsApp}
                className="bg-gradient-to-r from-zafco-gold to-zafco-orange text-white"
              >
                <Phone className="w-5 h-5 mr-2" />
                Schedule a Visit
              </Button>

              {office.mapUrl && (
                <div className="mt-8 rounded-2xl overflow-hidden">
                  <iframe
                    src={office.mapUrl}
                    width="100%"
                    height="300"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    title={`${office.name} Location`}
                  />
                </div>
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-xl"
            >
              <h3 className="text-xl font-bold text-zafco-blue font-montserrat mb-6">
                Book Your Consultation
              </h3>
              <ConsultationForm />
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OfficePage;
